<template>
  <div>
    <div v-if="displayCheckbox" class="info">
      <Info v-if="hasMaxSelectableXAxesNum()">
        <p>{{ $t('pages.companies.analysis.table.info', { number: maxSelectableXAxesNum }) }}</p>
      </Info>
      <div class="clearSelection">
        <button class="c-button__item c-button__item--outline" @click="clearSelection()">
          {{ $t('pages.companies.analysis.table.clearSelection') }}
        </button>
      </div>
    </div>
    <div v-show="items.length === 0 && !loading" v-text="$t('messages.noData')" />
    <div v-show="items.length !== 0" class="analysis-table">
      <div class="analysis-table-controller" :style="{ width: controller.width, top: controller.top }">
        <button
          v-if="controller.prev"
          type="button"
          class="analysis-table-controller-prev"
          @mousedown="scrollPrevStart" @mouseup="scrollEnd"
        >
          <KkIcon name="prev" color="#3A3B3F" />
        </button>
        <button
          v-if="controller.next"
          type="button"
          class="analysis-table-controller-next"
          @mousedown="scrollNextStart" @mouseup="scrollEnd"
        >
          <KkIcon name="next" color="#3A3B3F" />
        </button>
      </div>
      <div ref="container" class="analysis-table-container">
        <table ref="table">
          <thead ref="thead">
            <tr>
              <template v-for="(head, headIndex) in crossSearchFields" :key="head.name">
                <th v-if="headIndex === 0" ref="searchFields" class="fixed">
                  {{ head.name }}
                </th>
                <th v-else class="fixed" :style="{ 'left': firstSearchFieldWidth }">
                  {{ head.name }}
                </th>
              </template>
              <th v-for="head in staticHeads" :key="head.name">
                <button
                  v-if="activateSortFunction && !head.deactivateSort"
                  class="p-table__content__sort"
                  :class="orderCss(head.id)"
                  @click="sortTables(head.id)"
                >
                  {{ head.name }}
                </button>
                <span v-else>
                  {{ head.name }}
                </span>
              </th>
            </tr>
            <tr class="p-table__content__space">
              <td colspan="5" />
            </tr>
          </thead>
          <tbody v-show="!loading" ref="tbody">
            <tr v-if="headItem" class="head-item">
              <template v-for="(field, fieldIndex) in headItem.fields" :key="fieldIndex">
                <template v-if="fieldIndex === 0">
                  <td v-if="displayCheckbox" :key="fieldIndex" class="u-nowrap table-head fixed">
                    <ul class="c-checkbox">
                      <li class="c-checkbox__item">
                        <input
                          id="searchItemsHeader"
                          v-model="innerSearchItems"
                          type="checkbox"
                          class="c-checkbox__input"
                          :value="formatGraphId(headItem)"
                          :disabled="hasMaxSelectableXAxesNum() && maxSelectableXAxesNum !== 1 && searchItems.length >= maxSelectableXAxesNum && !innerSearchItems.find(i => i === headItem)"
                        >
                        <label class="c-checkbox__label" for="searchItemsHeader">
                          <span v-if="showTableRowIndex" class="index-number">1. </span>
                          {{ field.name }}
                        </label>
                      </li>
                    </ul>
                  </td>
                  <td v-else class="u-nowrap table-head fixed">
                    <span v-if="showTableRowIndex" class="index-number">1. </span>
                    {{ field.name }}
                  </td>
                </template>
                <td v-else class="table-head fixed" :style="{ 'left': displayCheckbox && firstSearchFieldWidth }">
                  {{ field.name }}
                </td>
              </template>
              <td v-for="head in staticHeads" :key="head.id" class="u-taR">
                <KkColumnSummaryHead :head-id="head.id" :head-item="headItem[head.id]" :caption="head.text" />
              </td>
            </tr>
            <CompetitorTableRow v-if="headItem && competitorMode && headItem.competitorData"
                                :item="headItem.competitorData[0]"
                                :compare-item="headItem"
                                :item-index="'display0'"
                                :static-heads="staticHeads"
                                :search-items="innerSearchItems"
                                :analysis-axis-field="crossSearchFields[0]"
                                :show-review-link="showReviewLink"
                                :value-formatter="valueFormatter"
                                @change-display-graphs="val => $emit('change-display-graphs', val)"
            />
            <template v-for="(item, itemIndex) in itemList" :key="itemIndex">
              <tr>
                <template v-for="(field, fieldIndex) in item.fields" :key="fieldIndex">
                  <template v-if="fieldIndex === 0">
                    <td v-if="displayCheckbox" class="u-nowrap table-head fixed" :style="tdStyle">
                      <div class="table-container">
                        <ul class="c-checkbox">
                          <li class="c-checkbox__item">
                            <input
                              :id="'searchItems' + itemIndex"
                              v-model="innerSearchItems"
                              type="checkbox"
                              class="c-checkbox__input"
                              :value="formatGraphId(item)"
                              :disabled="hasMaxSelectableXAxesNum() && maxSelectableXAxesNum !== 1 && searchItems.length >= maxSelectableXAxesNum && !innerSearchItems.find(i => i === item)"
                            >
                            <label class="c-checkbox__label" :for="'searchItems' + itemIndex">
                              <span v-if="showTableRowIndex" class="index-number">{{ itemIndex + 2 + '. ' }}</span>
                              <template v-if="enableFieldFiltering">
                                <a @click.prevent="changeIndividualAnalysisField(field)">{{ field.name }}</a>
                              </template>
                              <template v-else>
                                <span>{{ field.name }}</span>
                              </template>
                              <StoresCount v-if="item.fields.length === 1 && !!item.storeNames" :store-names="item.storeNames" :is-omit-single-store="isStoreAnalysisAxis" @open-modal="openModal" />
                            </label>
                          </li>
                        </ul>
                        <router-link
                          v-if="showReviewLink && item.fields.length === 1 && !hideReviewLinkForSpecificItem(item)"
                          :to="{
                            name: 'CompaniesReviews',
                            params: {
                              company_uuid: companyUuid,
                            },
                            query: {
                              analysis_type: adaptKey(item.fields[0].key),
                              analysis_id: adaptId(item.fields[0]),
                            }
                          }"
                          class="review-link"
                        >
                          <KkIcon name="review" color="#D2D5DB" />
                        </router-link>
                      </div>
                    </td>
                    <td v-else class="u-nowrap table-head fixed">
                      <span v-if="showTableRowIndex" class="index-number">{{ itemIndex + 2 + '. ' }}</span>
                      {{ field.name }}
                      <StoresCount v-if="item.fields.length === 1 && !!item.storeNames" :store-names="item.storeNames" :is-omit-single-store="isStoreAnalysisAxis" @open-modal="openModal" />
                    </td>
                  </template>
                  <td v-else class="u-nowrap table-head fixed" :style="{ 'left': displayCheckbox && firstSearchFieldWidth }">
                    <div class="table-container">
                      {{ field.name }}
                      <StoresCount v-if="!!item.storeNames" :store-names="item.storeNames" :is-omit-single-store="isStoreAnalysisAxis" @open-modal="openModal" />
                      <router-link
                        v-if="showReviewLink && !hideReviewLinkForSpecificItem(item)"
                        :to="{
                          name: 'CompaniesReviews',
                          params: {
                            company_uuid: companyUuid,
                          },
                          query: {
                            analysis_type: adaptKey(item.fields[0].key),
                            analysis_id: adaptId(item.fields[0]),
                            cross_analysis_type: adaptKey(field.key),
                            cross_analysis_id: adaptId(field)
                          }
                        }"
                        class="review-link"
                      >
                        <KkIcon name="review" color="#D2D5DB" />
                      </router-link>
                    </div>
                  </td>
                </template>
                <td v-for="head in staticHeads" :key="head.id" :class="head.align === 'left' ? '' : 'u-taR'">
                  <KkSearchQueryTd :head-id="head.id" :item="item[head.id]" />
                </td>
              </tr>
              <CompetitorTableRow v-for="(competitorItem,competitorItemIndex) in item.competitorData"
                                  v-show="competitorMode"
                                  :key="itemIndex + '_' + competitorItemIndex"
                                  :item="competitorItem"
                                  :compare-item="item"
                                  :item-index="itemIndex + '_' + competitorItemIndex"
                                  :static-heads="staticHeads"
                                  :search-items="innerSearchItems"
                                  :analysis-axis-field="crossSearchFields[0]"
                                  :show-review-link="showReviewLink"
                                  :value-formatter="valueFormatter"
                                  @change-display-graphs="val => $emit('change-display-graphs', val)"
                                  @open-modal="openModal"
              />
            </template>
          </tbody>
        </table>
      </div>
    </div>
    <Loading v-show="loading" />
    <StoresModal v-if="modal" :store-names="storeNames" @close="closeModal" />
  </div>
</template>

<script>
import Loading from '../Loading'
import Info from '../Info'
import StoresModal from './StoresModal.vue'
import StoresCount from './StoresCount.vue'
import CompetitorTableRow from './Reviews/CompetitorTableRow.vue'
import { toNumberLocaleString } from '../../modules/localeString'
import { KkIcon } from '../base'
import { generateGraphId } from '../../modules/analysis'
import { localeOrders } from '../../modules/analysis'
import { actionEvent } from 'src/biz_spa/modules/googleAnalytics'
import { KkColumnSummaryHead, KkSearchQueryTd } from '../../domains/analysis'

export default {
  name: 'CompaniesAnalysisReviewsTable',
  components: {
    CompetitorTableRow,
    Loading,
    Info,
    StoresModal,
    StoresCount,
    KkIcon,
    KkColumnSummaryHead,
    KkSearchQueryTd,
  },
  props: {
    loading: {
      type: Boolean,
      required: true,
    },
    crossSearchFields: {
      type: Array,
      required: true,
    },
    staticHeads: {
      type: Array,
      required: true,
    },
    items: {
      type: Array,
      required: true,
    },
    searchItems: {
      type: Array,
      required: false,
    },
    truncateStoreName: {
      type: Boolean,
      default: false,
    },
    competitorMode: {
      type: Boolean,
      default: false,
    },
    maxSelectableXAxesNum: {
      type: Number,
      default: null,
    },
    showReviewLink: {
      type: Boolean,
      default: false,
    },
    showTableRowIndex: {
      type: Boolean,
      default: true,
    },
    valueFormatter: {
      type: Function,
      default: (v) => {
        return Number(v) !== 0 ? toNumberLocaleString(v) : '-'
      },
    },
    isDefaultSort: {
      type: Boolean,
      default: true,
    },
    enableFieldFiltering: {
      type: Boolean,
      default: true,
    },
    activateSelectFunction: {
      type: Boolean,
      default: true,
    },
    activateSortFunction: {
      type: Boolean,
      default: true,
    },
    tdWidth: {
      type: String,
      default: '360px'
    }
  },
  emits: ['change-display-graphs', 'change-individual-analysis-field', 'sort'],
  data() {
    return {
      sortKey: '',
      sortOrder: '',
      modal: false,
      storeNames: null,
      firstSearchFieldWidth: '0px',
      resizeObserver: null,
      scrollIntervalId: null,
      controller: {
        width: '100%',
        top: '0px',
        targetWidth: '0px',
        prev: false,
        next: false,
        scrollAmount: '0',
      },
    }
  },
  computed: {
    companyUuid() {
      return this.$route.params.company_uuid
    },
    displayCheckbox() {
      return !!this.searchItems && this.activateSelectFunction
    },
    innerSearchItems: {
      get() {
        return this.searchItems
      },
      set(val) {
        if (this.maxSelectableXAxesNum === 1 && val.length > 1) val.shift()
        this.$emit('change-display-graphs', val)
      }
    },
    headItem() {
      const items = this.items[0]
      if (items && items.counts === 0) {
        items.scores = '-'
        if (Array.isArray(items.competitorData)) {
          items.competitorData = items.competitorData.map(item => {
            if (item.counts === 0) item.scores = '-'
            return item
          })
        }
      }
      return items
    },
    itemList() {
      let items = this.items.slice(1)
      items = this.formatData(items)

      switch (this.sortOrder) {
      case 'asc':
        items = items.sort((a, b) => {
          if (a[this.sortKey] < b[this.sortKey]) return -1
          if (a[this.sortKey] > b[this.sortKey]) return 1
          return 0
        })
        break
      case 'desc':
        items = items.sort((a, b) => {
          if (a[this.sortKey] > b[this.sortKey]) return -1
          if (a[this.sortKey] < b[this.sortKey]) return 1
          return 0
        })
        break
      default: // 最初は分析軸の名前でソート
        if (!items.length || !this.isDefaultSort) break

        // クロス分析の場合、セカンダリーからソートさせるためソート順の配列を用意して外側から順にソート
        var sortIndexOrderList = items[0].fields.map((_, i) => i).reverse()
        sortIndexOrderList.forEach((i) => {
          items = items.sort((a, b) => {
            // 未設定(id=-1)は一番下になるよう判定
            if (Number(a.fields[i].id) === -1) return 1
            if (Number(b.fields[i].id) === -1) return -1

            // 言語別はlocaleOrdersの順番にする
            if (a.fields[i].key === 'locale') {
              if (localeOrders.indexOf(a.fields[i].id) < localeOrders.indexOf(b.fields[i].id)) return -1
              if (localeOrders.indexOf(a.fields[i].id) > localeOrders.indexOf(b.fields[i].id)) return 1
            } else {
              if (a.fields[i].name < b.fields[i].name) return -1
              if (a.fields[i].name > b.fields[i].name) return 1
            }
            return 0
          })
        })
      }

      return items
    },
    isStoreAnalysisAxis() {
      return this.crossSearchFields && this.crossSearchFields.filter((field) => field.id === 'store').length !== 0
    },
    tdStyle() {
      return {
        whiteSpace: 'normal',
        minWidth: this.tdWidth,
        maxWidth: this.tdWidth,
      }
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
    this.setFirstSearchFieldWidth()
    this.tableObserver()
  },
  updated() {
    this.setFirstSearchFieldWidth()
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
    this.resizeObserver.disconnect()
    clearInterval(this.scrollIntervalId)
  },
  methods: {
    formatGraphId(item) {
      const graphId = generateGraphId(item)
      return graphId
    },
    formatData(items) {
      return items.map(item => {
        if (item.counts === 0) {
          item.scores = '-'
        } else if (typeof item.scores === 'number') {
          item.scores = item.scores.toFixed(1)
        }
        if (Array.isArray(item.competitorData)) {
          item.competitorData = item.competitorData.map(data => {
            if (data.counts === 0) data.scores = '-'
            return data
          })
        }
        return item
      })
    },
    changeIndividualAnalysisField(field) {
      this.$emit('change-individual-analysis-field', field)
    },
    sortTables(sortKey) {
      if (this.sortKey === sortKey) { // key が一致する場合は2回目押下時
        this.sortOrder = this.sortOrder === 'desc' ? 'asc' : 'desc'
      } else { // それ以外の場合は key 1回目押下時
        this.sortKey = sortKey
        this.sortOrder = 'desc'
      }
      this.$emit('sort', {
        sortId: this.sortKey,
        sortOrder: this.sortOrder,
      })
      switch (sortKey) {
      case 'scores':
        actionEvent('SortRevAnalReputation')
        break
      case 'counts':
        actionEvent('SortRevAnalPostNum')
        break
      case 'totalCounts':
        actionEvent('SortRevAnalPostCum')
        break
      }
    },
    orderCss(key) {
      if (!this.sortOrder) return '' // 初期値の場合は CSS なし
      if (this.sortKey === key) { // 一致する key の場合に CSS がつく
        return this.sortOrder === 'asc' ? 'is-asc' : 'is-desc'
      } else { // それ以外の key の場合は CSS なし
        return ''
      }
    },
    clearSelection() {
      this.$emit('change-display-graphs', [generateGraphId(this.items[0])])
    },
    openModal(storeNames) {
      this.storeNames = storeNames
      this.modal = true
    },
    closeModal() {
      this.storeNames = null
      this.modal = false
    },
    /**
     * 数値の3桁区切り
     * @param  { Number } value
     * @return { Number }
     */
    numberLocaleString(value) {
      return toNumberLocaleString(value)
    },
    handleResize() {
      this.setFirstSearchFieldWidth()
    },
    setFirstSearchFieldWidth() {
      const searchFields = this.$refs.searchFields
      if(!searchFields) return
      if(this.firstSearchFieldWidth === window.getComputedStyle(searchFields[0]).width) return
      this.firstSearchFieldWidth = window.getComputedStyle(searchFields[0]).width
    },
    hasMaxSelectableXAxesNum() {
      return this.maxSelectableXAxesNum != null
    },
    adaptKey(key) {
      switch(key) {
      case('store_id'):
        return 'stores'
      case('site_type'):
        return 'sites'
      case('brand_id'):
        return 'brands'
      case('franchisee_id'):
        return 'franchisees'
      case('user_company_id'):
        return 'user_companies'
      case('prefecture'):
        return 'prefectures'
      case('area_id'):
        return 'areas'
      case('locale'):
        return 'locales'
      case('original_tag_group_a_tag_id'):
        return 'original_tag_group_a'
      case('original_tag_group_b_tag_id'):
        return 'original_tag_group_b'
      case('original_tag_group_c_tag_id'):
        return 'original_tag_group_c'
      case('original_tag_group_d_tag_id'):
        return 'original_tag_group_d'
      case('original_tag_group_e_tag_id'):
        return 'original_tag_group_e'
      default:
        return key
      }
    },
    adaptId(field) {
      switch(field.key) {
      case('store_id'):
        return field.uuid
      default:
        return field.id
      }
    },
    scrollPrevStart() {
      const $container = this.$refs.container
      this.scrollIntervalId = setInterval(() => {
        if ( $container.scrollLeft === 0) {
          this.controller.prev = false
          clearInterval(this.scrollIntervalId)
        } else {
          $container.scrollLeft = $container.scrollLeft - 10
          this.controller.next = true
        }
      }, 10)
    },
    scrollNextStart() {
      const $container = this.$refs.container
      this.scrollIntervalId = setInterval(() => {
        if ($container.scrollLeft >= this.controller.scrollAmount) {
          this.controller.next = false
          clearInterval(this.scrollIntervalId)
        } else {
          $container.scrollLeft = $container.scrollLeft + 10
          this.controller.prev = true
        }
      }, 10)
    },
    scrollEnd() {
      clearInterval(this.scrollIntervalId)
    },
    tableObserver() {
      const $container = this.$refs.container
      const $table = this.$refs.table
      const $thead = this.$refs.thead
      const $tbody = this.$refs.tbody
      if (this.resizeObserver) return
      this.resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          if (entry.target.nodeName === 'TABLE') {
            if (!$tbody.firstElementChild) return
            this.controller.top = `${ $thead.offsetHeight + $tbody.firstElementChild.offsetHeight}px`
          }
          const $tr = $tbody.firstElementChild
          const $td = $tr.children

          // コントローラー幅の計算
          const controllerWidth = Array.from($td).reduce((prev, current) => {
            return current.classList.contains('fixed') ? prev - current.offsetWidth : prev
          }, $container.offsetWidth)
          this.controller.width = `${ controllerWidth }px`

          // スクロール可能か
          if ($container.clientWidth < $table.clientWidth) this.controller.next = true

          // スクロール可能な量の計算
          const fixedWidth = Array.from($td).reduce((prev, current) => {
            return current.classList.contains('fixed') ? prev + current.offsetWidth : prev
          }, 0)
          this.controller.scrollAmount = $table.clientWidth - fixedWidth - controllerWidth
        }
      })
      this.resizeObserver.observe($table)
      this.resizeObserver.observe($tbody)
      this.resizeObserver.observe($container)
    },
    // 特定のレコードは口コミ一覧の絞り込みに対応してないためリンクを出さない
    hideReviewLinkForSpecificItem(item) {
      // 言語別の軸で未分類(-1)のものは口コミ一覧で絞り込みできないため
      return item.fields.some(field => field.key === 'locale' && field.id === '-1')
    }
  }
}
</script>

<style lang="scss" scoped>
.info {
  display: flex;
}
.clearSelection {
  margin-left: 8px;
  button {
    padding: 16px;
    font-weight: normal;
    font-size: 12px;
  }
}
.analysis-table {
  position: relative;
  table {
    position: relative;
    z-index: 0;
  }
  thead {
    position: sticky;
    top: 0;
    z-index: 2;
  }
  &-container {
    max-height: 84vh;
    overflow-x: auto;
    overflow-y: auto;
  }
  &-controller {
    position: absolute;
    right: 0;
    z-index: 3;
    &-prev,
    &-next {
      opacity: .3;
      position: absolute;
      top: 0;
    }
    &-prev {
      left: 0;
      transform: translate(-50%, -50%);
      width: 40px;
    }
    &-next {
      right: 0;
      transform: translate(50%, -50%);
      width: 40px;
    }
    @media screen and (max-width: 768px) {
      display: none;
    }
  }
}
.head-item {
  .u-taR {
    background-color: #F8F9FA;
    font-weight: bold;
    font-size: 18px;
    span {
      display: block;
      font-weight: normal;
      font-size: 10px;
    }
  }
}
tbody .table-head {
  background-color: #F8F9FA;
  &:not(:first-child) {
    border-left: 0;
  }
}
.fixed {
  position: sticky;
  left: 0;
  z-index: 1;
  @media screen and (max-width: 768px) {
    position: initial;
  }
}
.index-number {
  margin: 0 8px;
  font-weight: bold;
}
.score-text {
  width: 38px;
}
.table-container {
  display: flex;
  align-items: center;
}
.review-link {
  width: 24px;
  margin-left: auto;
  padding-left: 8px;
}
</style>
